import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Alert, Badge, Modal } from 'react-bootstrap';
import { getAllSettings, updateStyleTariffPrices, getDoorAndWindowPrices } from '../../../../services/settingsService';
import { getAllRepairStyles } from '../../../../services/repairStyleService';
import { getAllRepairTariffs } from '../../../../services/repairTariffService';

const SettingsManager = () => {
  const [settings, setSettings] = useState({
    doorPrices: [],
    windowPrices: [],
    other: []
  });
  const [repairStyles, setRepairStyles] = useState([]);
  const [repairTariffs, setRepairTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editValues, setEditValues] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [newPriceConfig, setNewPriceConfig] = useState({
    type: 'door',
    styleId: '',
    tariffId: '',
    materialPrice: 0,
    servicePrice: 0
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    console.log('Component mounted, fetching data...');
    fetchSettings();
    fetchRepairStyles();
    fetchRepairTariffs();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      console.log('Fetching settings...');
      const response = await getAllSettings();
      console.log('Fetched settings:', response);

      // Initialize edit values for all settings
      const initialValues = {};
      Object.values(response).flat().forEach(setting => {
        initialValues[setting.key] = setting.value;
      });
      
      setSettings(response);
      setEditValues(initialValues);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.response?.data?.message || 'Failed to fetch settings');
      setLoading(false);
    }
  };

  const fetchRepairStyles = async () => {
    try {
      const response = await getAllRepairStyles();
      console.log('Raw repair styles response:', response);
      const activeStyles = response.filter(style => style.status === true);
      console.log('Active styles:', {
        count: activeStyles.length,
        styles: activeStyles.map(s => ({
          id: s.id,
          name: s.name,
          normalizedName: normalizeKey(getStyleName(s))
        }))
      });
      setRepairStyles(activeStyles);
    } catch (err) {
      console.error('Error fetching repair styles:', err);
    }
  };

  const fetchRepairTariffs = async () => {
    try {
      const response = await getAllRepairTariffs();
      console.log('Raw repair tariffs response:', response);
      const activeTariffs = response.filter(tariff => tariff.status === true);
      console.log('Active tariffs:', {
        count: activeTariffs.length,
        tariffs: activeTariffs.map(t => ({
          id: t.id,
          name: t.name,
          normalizedName: normalizeKey(getTariffName(t))
        }))
      });
      setRepairTariffs(activeTariffs);
    } catch (err) {
      console.error('Error fetching repair tariffs:', err);
    }
  };

  const handleInputChange = (key, value) => {
    setEditValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNewPriceInputChange = (field, value) => {
    setNewPriceConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStyleName = (style) => {
    if (!style) return '';
    if (typeof style.name === 'object') {
      return style.name.az || style.name.text || style.name.en || 'unnamed-style';
    }
    return style.name || 'unnamed-style';
  };

  const getTariffName = (tariff) => {
    if (!tariff) return '';
    if (typeof tariff.name === 'object') {
      return tariff.name.az || tariff.name.text || tariff.name.en || 'unnamed-tariff';
    }
    return tariff.name || 'unnamed-tariff';
  };

  const normalizeKey = (str) => {
    if (!str) return '';
    return str.toLowerCase()
      .replace(/ə/g, 'e')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/[^a-z0-9\-]/g, '')
      .replace(/\s+/g, '-');
  };

  const handleSave = async (setting) => {
    try {
      const newValue = editValues[setting.key];
      if (String(newValue) === String(setting.value)) {
        return; // No change
      }

      setLoading(true);

      // Extract style and tariff info from key
      const keyParts = setting.key.split('_');
      if (keyParts.length >= 3 && (keyParts[0].includes('door') || keyParts[0].includes('window'))) {
        const type = keyParts[0].includes('door') ? 'door' : 'window';
        const styleKey = keyParts[1];
        const tariffKey = keyParts[2];

        // Find style and tariff by normalized name
        const style = repairStyles.find(s => normalizeKey(getStyleName(s)) === styleKey);
        const tariff = repairTariffs.find(t => normalizeKey(getTariffName(t)) === tariffKey);

        console.log('Matching style and tariff:', {
          styleKey,
          tariffKey,
          style: style ? getStyleName(style) : 'not found',
          tariff: tariff ? getTariffName(tariff) : 'not found',
          allStyles: repairStyles.map(s => ({ id: s.id, name: getStyleName(s), normalized: normalizeKey(getStyleName(s)) })),
          allTariffs: repairTariffs.map(t => ({ id: t.id, name: getTariffName(t), normalized: normalizeKey(getTariffName(t)) }))
        });

        if (!style || !tariff) {
          throw new Error(`Style (${styleKey}) or tariff (${tariffKey}) not found. Available styles: ${repairStyles.map(s => normalizeKey(getStyleName(s))).join(', ')}. Available tariffs: ${repairTariffs.map(t => normalizeKey(getTariffName(t))).join(', ')}`);
        }

        const isServicePrice = setting.key.includes('Service');
        const prices = {
          materialPrice: isServicePrice ? undefined : parseFloat(newValue),
          servicePrice: isServicePrice ? parseFloat(newValue) : undefined
        };

        // Get current prices to determine if we should POST or PUT
        const currentPrices = await getDoorAndWindowPrices(style.id, tariff.id);
        const method = currentPrices ? 'PUT' : 'POST';

        await updateStyleTariffPrices({
          type,
          styleId: style.id,
          tariffId: tariff.id,
          prices,
          method
        });

        setSuccessMessage(`Setting ${method === 'POST' ? 'created' : 'updated'} successfully`);
      }

      await fetchSettings(); // Refresh settings
    } catch (err) {
      console.error('Error saving setting:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 3000);
    }
  };

  const handleAddNewPrice = async () => {
    try {
      const { type, styleId, tariffId, materialPrice, servicePrice } = newPriceConfig;

      if (!styleId || !tariffId) {
        setError('Please select both style and tariff');
        return;
      }

      if (materialPrice < 0 || servicePrice < 0) {
        setError('Prices cannot be negative');
        return;
      }

      setLoading(true);

      await updateStyleTariffPrices({
        type,
        styleId,
        tariffId,
        prices: {
          materialPrice: parseFloat(materialPrice),
          servicePrice: parseFloat(servicePrice)
        }
      });

      setSuccessMessage(`New ${type} prices added successfully`);
      setShowModal(false);
      await fetchSettings(); // Refresh settings

      // Reset form
      setNewPriceConfig({
        type: 'door',
        styleId: '',
        tariffId: '',
        materialPrice: 0,
        servicePrice: 0
      });
    } catch (err) {
      console.error('Error adding new price:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setError(null);
    setValidationErrors({});
  };

  const renderSettingInput = (setting) => {
    return (
      <Form.Control
        type="number"
        value={editValues[setting.key] || ''}
        onChange={(e) => handleInputChange(setting.key, e.target.value)}
      />
    );
  };

  const renderPriceTable = (prices, type) => {
    if (loading) {
      return <p>Loading {type} price settings...</p>;
    }

    if (!prices.length) {
      return <p>No {type} price settings found.</p>;
    }

    // Group prices by style and tariff
    const groupedPrices = prices.reduce((acc, setting) => {
      const keyParts = setting.key.split('_');
      if (keyParts.length < 3) return acc;

      const styleKey = keyParts[1];
      const tariffKey = keyParts[2];
      const isService = setting.key.includes('Service');

      const key = `${styleKey}_${tariffKey}`;
      if (!acc[key]) {
        acc[key] = {
          styleKey,
          tariffKey,
          materialPrice: null,
          servicePrice: null
        };
      }

      if (isService) {
        acc[key].servicePrice = setting;
      } else {
        acc[key].materialPrice = setting;
      }

      return acc;
    }, {});

    return (
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Style & Tariff</th>
            <th>Material Price</th>
            <th>Service Price</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(groupedPrices).map(({ styleKey, tariffKey, materialPrice, servicePrice }) => {
            // Find matching style and tariff
            const style = repairStyles.find(s => normalizeKey(getStyleName(s)) === styleKey);
            const tariff = repairTariffs.find(t => normalizeKey(getTariffName(t)) === tariffKey);

            console.log('Rendering row:', {
              styleKey,
              tariffKey,
              foundStyle: style ? getStyleName(style) : 'not found',
              foundTariff: tariff ? getTariffName(tariff) : 'not found',
              style,
              tariff
            });

            const styleName = style ? getStyleName(style) : styleKey;
            const tariffName = tariff ? getTariffName(tariff) : tariffKey;

            return (
              <tr key={`${styleKey}_${tariffKey}`}>
                <td>
                  <strong>{styleName} / {tariffName}</strong>
                  {(!style || !tariff) && (
                    <Badge bg="warning" className="ms-2">Not Found</Badge>
                  )}
                </td>
                <td>{materialPrice ? renderSettingInput(materialPrice) : 'N/A'}</td>
                <td>{servicePrice ? renderSettingInput(servicePrice) : 'N/A'}</td>
                <td>
                  {materialPrice && (
                    <Button 
                      variant="primary" 
                      size="sm"
                      className="me-2"
                      onClick={() => handleSave(materialPrice)}
                      disabled={loading || !style || !tariff}
                    >
                      Save Material
                    </Button>
                  )}
                  {servicePrice && (
                    <Button 
                      variant="info" 
                      size="sm"
                      onClick={() => handleSave(servicePrice)}
                      disabled={loading || !style || !tariff}
                    >
                      Save Service
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const renderModal = () => (
    <Modal show={showModal} onHide={handleModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add New Price Setting</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select 
              value={newPriceConfig.type}
              onChange={(e) => handleNewPriceInputChange('type', e.target.value)}
            >
              <option value="door">Door</option>
              <option value="window">Window</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Repair Style</Form.Label>
            <Form.Select 
              value={newPriceConfig.styleId}
              onChange={(e) => handleNewPriceInputChange('styleId', e.target.value)}
              isInvalid={!!validationErrors.styleId}
            >
              <option value="">Select...</option>
              {repairStyles.map(style => (
                <option key={style.id} value={style.id}>
                  {getStyleName(style)}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.styleId}
            </Form.Control.Feedback>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Repair Tariff</Form.Label>
            <Form.Select
              value={newPriceConfig.tariffId}
              onChange={(e) => handleNewPriceInputChange('tariffId', e.target.value)}
              isInvalid={!!validationErrors.tariffId}
            >
              <option value="">Select...</option>
              {repairTariffs.map(tariff => (
                <option key={tariff.id} value={tariff.id}>
                  {getTariffName(tariff)}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.tariffId}
            </Form.Control.Feedback>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Material Price</Form.Label>
            <Form.Control
              type="number"
              value={newPriceConfig.materialPrice}
              onChange={(e) => handleNewPriceInputChange('materialPrice', e.target.value)}
              isInvalid={!!validationErrors.materialPrice}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.materialPrice}
            </Form.Control.Feedback>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Service Price</Form.Label>
            <Form.Control
              type="number"
              value={newPriceConfig.servicePrice}
              onChange={(e) => handleNewPriceInputChange('servicePrice', e.target.value)}
              isInvalid={!!validationErrors.servicePrice}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.servicePrice}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAddNewPrice}
          disabled={loading}
        >
          {loading ? 'Adding...' : 'Add Price'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <Container fluid>
      <h2 className="mb-4">System Settings</h2>
      
      {successMessage && (
        <Alert variant="success">{successMessage}</Alert>
      )}
      
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      {renderModal()}
      
      <Card className="mb-4">
        <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
          Door Price Settings
          <Button variant="success" size="sm" onClick={() => {
            setNewPriceConfig({...newPriceConfig, type: 'door'});
            setShowModal(true);
          }}>
            + Add New Door Price
          </Button>
        </Card.Header>
        <Card.Body>
          {renderPriceTable(settings.doorPrices, 'door')}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
          Window Price Settings
          <Button variant="success" size="sm" onClick={() => {
            setNewPriceConfig({...newPriceConfig, type: 'window'});
            setShowModal(true);
          }}>
            + Add New Window Price
          </Button>
        </Card.Header>
        <Card.Body>
          {renderPriceTable(settings.windowPrices, 'window')}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header as="h5">Other System Settings</Card.Header>
        <Card.Body>
          {loading ? (
            <p>Loading settings...</p>
          ) : !settings.other.length ? (
            <p>No other settings found.</p>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Setting</th>
                  <th>Description</th>
                  <th>Value</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {settings.other.map(setting => (
                  <tr key={setting.key}>
                    <td>{setting.key}</td>
                    <td>{setting.description}</td>
                    <td>{renderSettingInput(setting)}</td>
                    <td>
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={() => handleSave(setting)}
                        disabled={loading}
                      >
                        Save
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SettingsManager; 